import { useState } from "react";
import axios from "axios";
import { Heart, Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import { Reveal, Overline } from "@/components/site/Reveal";
import { API } from "@/lib/site";
import { useLang } from "@/context/LanguageContext";

export const GetInvolved = ({ onDonate }) => {
  const { t } = useLang();
  const roles = t("involved.roles");
  const [form, setForm] = useState({ name: "", email: "", roleIndex: 0, organization: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error(t("involved.fillError"));
      return;
    }
    setLoading(true);
    const roleSlugs = ["volunteer", "partner", "sponsor", "other"];
    try {
      await axios.post(`${API}/involve`, {
        name: form.name, email: form.email, role: roleSlugs[form.roleIndex] || "other",
        organization: form.organization, message: form.message,
      });
      setDone(true);
      toast.success(t("involved.thanks"));
      setForm({ name: "", email: "", roleIndex: 0, organization: "", message: "" });
      setTimeout(() => setDone(false), 4000);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("donate.error"));
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "mt-2 w-full px-4 py-3 bg-white border border-[#0A1128]/20 focus:border-[#D4AF37] outline-none text-[#0A1128]";

  return (
    <section id="involved" data-testid="get-involved-section" className="relative bg-[#F8F9FA] py-24 md:py-36">
      <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10">
        <Reveal>
          <div className="max-w-3xl">
            <Overline>{t("involved.overline")}</Overline>
            <h2 className="mt-6 font-display text-4xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tight text-[#0A1128]">
              {t("involved.titleA")}<span className="italic text-[#D4AF37]">{t("involved.titleB")}</span>
            </h2>
          </div>
        </Reveal>

        <div className="mt-16 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5 space-y-6">
            <Reveal delay={0.1}>
              <div className="bg-[#D4AF37] p-8 md:p-10">
                <Heart size={28} className="text-[#0A1128]" fill="#0A1128" />
                <h3 className="mt-5 font-display text-3xl text-[#0A1128]">{t("involved.donateTitle")}</h3>
                <p className="mt-3 text-[#0A1128]/80 leading-relaxed">{t("involved.donateBody")}</p>
                <button data-testid="cta-donate-button" onClick={onDonate} className="mt-6 w-full py-4 bg-[#0A1128] text-white font-semibold hover:bg-[#1D3557] transition-colors">
                  {t("involved.donateBtn")}
                </button>
              </div>
            </Reveal>

            <Reveal delay={0.2}>
              <div className="bg-white border border-[#0A1128]/15 p-8 flex items-center gap-6">
                <div className="border border-[#0A1128]/10 p-2 shrink-0">
                  <img data-testid="zelle-qr" src="/zelle-qr.png" alt="Zelle QR code" className="w-28 h-28 object-contain" />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] text-[#B8901F] font-semibold">{t("involved.zelleLabel")}</p>
                  <p className="mt-2 font-display text-xl text-[#0A1128]">Herbert Joseph</p>
                  <p className="text-[#4B5563] text-sm mt-1">{t("involved.zelleScan")}</p>
                  <p data-testid="zelle-phone" className="mt-1 font-semibold text-[#0A1128] tracking-wide">(347) 444-7776</p>
                </div>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <Reveal delay={0.15}>
              <form data-testid="involve-form" onSubmit={submit} className="bg-white border border-[#0A1128]/15 p-8 md:p-10">
                <h3 className="font-display text-2xl md:text-3xl text-[#0A1128]">{t("involved.formTitle")}</h3>
                <p className="mt-2 text-[#4B5563]">{t("involved.formSub")}</p>

                <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label className="text-xs uppercase tracking-widest text-[#4B5563]">{t("involved.name")} *</label>
                    <input data-testid="involve-name" value={form.name} onChange={update("name")} className={inputCls} />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-widest text-[#4B5563]">{t("involved.email")} *</label>
                    <input data-testid="involve-email" type="email" value={form.email} onChange={update("email")} className={inputCls} />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-widest text-[#4B5563]">{t("involved.iam")} *</label>
                    <select data-testid="involve-role" value={form.roleIndex} onChange={(e) => setForm((f) => ({ ...f, roleIndex: parseInt(e.target.value) }))} className={`${inputCls} bg-white`}>
                      {roles.map((r, i) => <option key={i} value={i}>{r}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-widest text-[#4B5563]">{t("involved.org")}</label>
                    <input data-testid="involve-org" value={form.organization} onChange={update("organization")} className={inputCls} />
                  </div>
                </div>
                <div className="mt-5">
                  <label className="text-xs uppercase tracking-widest text-[#4B5563]">{t("involved.message")} *</label>
                  <textarea data-testid="involve-message" rows={4} value={form.message} onChange={update("message")} className={`${inputCls} resize-none`} />
                </div>

                <button data-testid="involve-submit" type="submit" disabled={loading || done} className="mt-7 px-8 py-4 bg-[#0A1128] text-white font-semibold hover:bg-[#1D3557] transition-colors disabled:opacity-70 flex items-center gap-2">
                  {loading ? (<><Loader2 className="animate-spin" size={18} /> {t("involved.sending")}</>)
                    : done ? (<><Check size={18} /> {t("involved.submitted")}</>)
                    : t("involved.submit")}
                </button>
              </form>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};
